/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitShiftProps {
  id: string;
  name: string;
  startTimeInMinutes: number;
  endTimeInMinutes: number;
  unitId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class UnitShiftRecord extends Record<UnitShiftProps>({
  id: null as any,
  name: null as any,
  startTimeInMinutes: null as any,
  endTimeInMinutes: null as any,
  unitId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    startTimeInMinutes: number;
    endTimeInMinutes: number;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): UnitShiftRecord {
    if (json == null) {
      throw new Error('UnitShiftRecord.fromJS: parameter cannot be null');
    }
    return new UnitShiftRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitShiftJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'UnitShiftJson.name'),
      startTimeInMinutes: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'startTimeInMinutes', 0),
        'UnitShiftJson.startTimeInMinutes',
      ),
      endTimeInMinutes: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'endTimeInMinutes', 0),
        'UnitShiftJson.endTimeInMinutes',
      ),
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'UnitShiftJson.unitId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'UnitShiftJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    startTimeInMinutes: number;
    endTimeInMinutes: number;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [UnitShiftRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitShiftRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitShiftRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitShiftJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'UnitShiftJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      startTimeInMinutes: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'startTimeInMinutes', 0),
            'UnitShiftJson.startTimeInMinutes',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      endTimeInMinutes: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'endTimeInMinutes', 0),
            'UnitShiftJson.endTimeInMinutes',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'UnitShiftJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'UnitShiftJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: UnitShiftProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitShiftRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitShiftViewModelBase implements UnitShiftProps {
  protected readonly _props: UnitShiftProps;
  private _json?: UnitShiftProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get startTimeInMinutes(): number {
    return this._props.startTimeInMinutes;
  }
  get endTimeInMinutes(): number {
    return this._props.endTimeInMinutes;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: UnitShiftProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        startTimeInMinutes: this._props.startTimeInMinutes,
        endTimeInMinutes: this._props.endTimeInMinutes,
        unitId: this._props.unitId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllUnitShiftEntities = (state: AppState) =>
  state.entityStores.unitShift.entities;

export const selectUnitShiftById = (id: string) => (state: AppState) =>
  state.entityStores.unitShift.entities.get(id);

export const selectUnitShiftMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.unitShift.meta.get(id);

export const selectUnitShiftAndMetadataById = (id: string) =>
  createSelector(selectUnitShiftById(id), selectUnitShiftMetadataById(id), (unitShift, meta) => ({
    unitShift,
    meta,
  }));

export function selectUnitShiftEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllUnitShiftEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, UnitShiftRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectUnitShiftEntitiesByName(name: string) {
  return createSelector(selectAllUnitShiftEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectUnitShiftEntitiesByStartTimeInMinutes(startTimeInMinutes: number) {
  return createSelector(selectAllUnitShiftEntities, (entities) =>
    entities.filter((e) => e.startTimeInMinutes === startTimeInMinutes).valueSeq(),
  );
}

export function selectUnitShiftEntitiesByEndTimeInMinutes(endTimeInMinutes: number) {
  return createSelector(selectAllUnitShiftEntities, (entities) =>
    entities.filter((e) => e.endTimeInMinutes === endTimeInMinutes).valueSeq(),
  );
}

export function selectUnitShiftEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllUnitShiftEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectUnitShiftEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllUnitShiftEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
